import isEqual from 'lodash.isequal';
import transform from 'lodash.transform';
import isObject from 'lodash.isobject';
import debug from 'debug';

// Configuration of logs with namespaces for better organization
const logTrailer = debug('rois:difference:trailer');
const logVehicle = debug('rois:difference:vehicle');
const logDifference = debug('rois:difference');

// Enable logs only in development
if (import.meta.env.DEV) {
  debug.enable('rois:difference*');
}

interface Vehicle {
  registVehicle?: string;
  [key: string]: unknown;
}

/**
 *
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(object: object, base: object): object {
  function changes(object: object, base: object) {
    return transform(object, function (result: never, value, key) {
      if (!isEqual(value, base[key])) {
        result[key] =
          isObject(value) && isObject(base[key])
            ? changes(value, base[key])
            : value;
      }
    });
  }
  // 🔴 `as object`, no `<object>`. El cast con angulo es TS valido en un .ts,
  // pero el parser de Babel que usa react-docgen lleva el plugin de JSX
  // activado y lee `<object>` como la apertura de una etiqueta: sigue
  // tragando y se rinde diez lineas mas abajo. Eso tumbaba `build-storybook`
  // entero con un «Unexpected token» que señalaba a la linea equivocada.
  return changes(object, base) as object;
}

/**
 * Deep diff between two objects, detecting added, changed, and removed elements.
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Object representing the diff
 */
export function differenceWithRemove(
  object: Record<string, unknown>,
  base: Record<string, unknown>
): object {
  function changes(
    object: Record<string, unknown>,
    base: Record<string, unknown>
  ) {
    const result = transform(
      base,
      (result: Record<string, unknown>, value, key) => {
        if (!(key in object)) {
          // Key was removed
          result[key] = isObject(value) ? '__REMOVED__' : `__REMOVED__${value}`;
        } else if (!isEqual(value, object[key])) {
          // If the value has changed, check if it's a nested object
          if (Array.isArray(value) && Array.isArray(object[key])) {
            result[key] = arrayDiff(
              object[key] as unknown[],
              value as unknown[]
            );
          } else {
            result[key] =
              isObject(value) && isObject(object[key])
                ? changes(
                    object[key] as Record<string, unknown>,
                    value as Record<string, unknown>
                  )
                : object[key];
          }
        }
      }
    );

    // Include keys from `object` that are not in `base` (added keys)
    Object.keys(object).forEach((key) => {
      if (!(key in base)) {
        result[key] = object[key];
      }
    });

    return result;
  }

  function arrayDiff(objectArray: unknown[], baseArray: unknown[]) {
    const removed = baseArray
      .filter((item) => !objectArray.includes(item))
      .map((item) => `__REMOVED__${item}`);
    const added = objectArray.filter((item) => !baseArray.includes(item));
    return [...removed, ...added];
  }

  return changes(
    object as Record<string, unknown>,
    base as Record<string, unknown>
  );
}

/**
 * Split object differences into four categories:
 * 1. Differences: Contains all changes (added, modified) across all fields.
 * 2. Removed: Only `letters` and `vehicles` that were removed.
 * 3. Added: Only new `letters` and `vehicles` that were added.
 * 4. Modified: Only `letters` and `vehicles` that were modified.
 */
export function splitDifference(object: object, base: object) {
  const differences: any = {};

  const removed: any = {
    letters: [],
    vehicles: {},
    registVehicle: [],
    registTrailer: [],
  };

  const added: any = {
    letters: [],
    vehicles: {},
    registVehicle: [],
    registTrailer: [],
  };

  const modified: any = {
    letters: [],
    vehicles: {},
    registVehicleChanges: [], // New array to track plate changes in vehicles
    registTrailerChanges: [], // New array to track plate changes in trailers
  };

  function processDifferences(object: any, base: any, result: any) {
    return transform(base, (res: any, value, key) => {
      if (!(key in object)) {
        // Removed elements for `letters` and `vehicles`
        if (key === 'letters') {
          removed.letters.push(...base[key]);
        } else if (key === 'vehicles') {
          Object.entries(base[key] as Record<string, Vehicle>).forEach(
            ([vehicleKey, vehicleValue]) => {
              removed.vehicles[vehicleKey] = vehicleValue;
              if (vehicleValue.registVehicle) {
                removed.registVehicle.push(vehicleValue.registVehicle);
              }
              if (vehicleValue.registTrailer) {
                removed.registTrailer.push(vehicleValue.registTrailer);
              }
            }
          );
        } else {
          // Include other changes in `differences`
          result[key] = value;
        }
      } else if (!isEqual(value, object[key])) {
        // Modified or nested differences
        if (key === 'letters') {
          const { addedArray, removedArray, modifiedArray } = arrayDiff(
            object[key],
            base[key]
          );
          added.letters.push(...addedArray);
          removed.letters.push(...removedArray);
          modified.letters.push(...modifiedArray);
          result[key] = [...object[key]];
        } else if (key === 'vehicles') {
          Object.keys(base[key]).forEach((vehicleKey) => {
            if (!(vehicleKey in object[key])) {
              removed.vehicles[vehicleKey] = base[key][vehicleKey];
              if (base[key][vehicleKey].registVehicle) {
                removed.registVehicle.push(base[key][vehicleKey].registVehicle);
              }
              if (base[key][vehicleKey].registTrailer) {
                removed.registTrailer.push(base[key][vehicleKey].registTrailer);
              }
            }
          });
          Object.keys(object[key]).forEach((vehicleKey) => {
            if (!(vehicleKey in base[key])) {
              // Check if this "new" vehicle is actually one with a changed plate
              const plateChange = modified.registVehicleChanges.find(
                (change: any) =>
                  change.newPlate === object[key][vehicleKey].registVehicle
              );

              if (!plateChange) {
                // It is really a new vehicle, process normally
                added.vehicles[vehicleKey] = object[key][vehicleKey];
                if (object[key][vehicleKey].registVehicle) {
                  added.registVehicle.push(
                    object[key][vehicleKey].registVehicle
                  );
                }
                if (object[key][vehicleKey].registTrailer) {
                  added.registTrailer.push(
                    object[key][vehicleKey].registTrailer
                  );
                }
                // Keep these lines from the original code
                result[key] = result[key] || {};
                result[key][vehicleKey] = object[key][vehicleKey];
              } else {
                logDifference(
                  `⚠️ Vehículo con matrícula cambiada no añadido como nuevo: ${object[key][vehicleKey].registVehicle}`
                );
              }
            } else if (
              !isEqual(object[key][vehicleKey], base[key][vehicleKey])
            ) {
              // Modified vehicle
              modified.vehicles[vehicleKey] = object[key][vehicleKey];

              // Detect plate change
              if (
                base[key][vehicleKey].registVehicle !==
                object[key][vehicleKey].registVehicle
              ) {
                // Save the original plate in the modified vehicle
                modified.vehicles[vehicleKey]._originalRegistVehicle =
                  base[key][vehicleKey].registVehicle;

                // Add to our new tracking array
                modified.registVehicleChanges.push({
                  key: vehicleKey,
                  oldPlate: base[key][vehicleKey].registVehicle,
                  newPlate: object[key][vehicleKey].registVehicle,
                });

                logDifference(
                  `📝 Cambio de matrícula detectado: ${base[key][vehicleKey].registVehicle} -> ${object[key][vehicleKey].registVehicle}`
                );
              }

              // Detect if the trailer plate has changed
              if (
                base[key][vehicleKey].registTrailer &&
                object[key][vehicleKey].registTrailer &&
                base[key][vehicleKey].registTrailer !==
                  object[key][vehicleKey].registTrailer
              ) {
                // Register the trailer plate change
                logTrailer(
                  `🔄 Trailer cambió matrícula: ${base[key][vehicleKey].registTrailer} -> ${object[key][vehicleKey].registTrailer}`
                );

                // Save the original trailer plate
                modified.vehicles[vehicleKey]._originalRegistTrailer =
                  base[key][vehicleKey].registTrailer;

                // Add to our trailer tracking array
                modified.registTrailerChanges.push({
                  key: vehicleKey,
                  oldPlate: base[key][vehicleKey].registTrailer,
                  newPlate: object[key][vehicleKey].registTrailer,
                });

                logDifference(
                  `📝 Cambio de matrícula de trailer detectado: ${base[key][vehicleKey].registTrailer} -> ${object[key][vehicleKey].registTrailer}`
                );
              }

              // Check if a trailer has been added or removed (existing code)
              if (
                base[key][vehicleKey].registTrailer &&
                !object[key][vehicleKey].registTrailer
              ) {
                // Check if it is part of a trailer plate change
                const isTrailerPlateChangeTracked =
                  modified.registTrailerChanges.some(
                    (change: { oldPlate: string; newPlate: string }) =>
                      change.oldPlate === base[key][vehicleKey].registTrailer
                  );

                if (!isTrailerPlateChangeTracked) {
                  removed.registTrailer.push(
                    base[key][vehicleKey].registTrailer
                  );
                } else {
                  logTrailer(
                    `ℹ️ Matrícula de trailer cambiada no marcada para eliminación: ${base[key][vehicleKey].registTrailer}`
                  );
                }
              }

              // Also check if a trailer has been added
              if (
                !base[key][vehicleKey].registTrailer &&
                object[key][vehicleKey].registTrailer
              ) {
                // If a trailer is added to an existing vehicle, consider it as added
                // It does not exist in base.registTrailer but does in object.registTrailer
                if (
                  !removed.registTrailer.includes(
                    object[key][vehicleKey].registTrailer
                  )
                ) {
                  // Make sure it has not been registered as removed before
                  logTrailer(
                    `🆕 Trailer añadido a vehículo existente (${object[key][vehicleKey].registTrailer}): Este trailer se procesará como un vehículo añadido`
                  );

                  // Add this trailer to the list of added trailers so it is processed correctly
                  // This is key for the trailer to be created and the parent-child relationship established
                  if (!added.registTrailer) {
                    added.registTrailer = [];
                  }
                  added.registTrailer.push(
                    object[key][vehicleKey].registTrailer
                  );
                }
              }

              // Check if the vehicle plate has changed
              if (
                base[key][vehicleKey].registVehicle !==
                object[key][vehicleKey].registVehicle
              ) {
                // Register the plate change to update relationships correctly
                logVehicle(
                  `🔄 Vehículo cambió matrícula: ${base[key][vehicleKey].registVehicle} -> ${object[key][vehicleKey].registVehicle}`
                );

                // If the plate has changed, consider the vehicle with the old plate as removed
                // and the vehicle with the new plate as added
                const isPlateChangeTracked = modified.registVehicleChanges.some(
                  (change: { oldPlate: string; newPlate: string }) =>
                    change.oldPlate === base[key][vehicleKey].registVehicle &&
                    change.newPlate === object[key][vehicleKey].registVehicle
                );

                if (
                  !isPlateChangeTracked &&
                  base[key][vehicleKey].registVehicle
                ) {
                  // Only if it is not a detected plate change do we consider it as removed
                  removed.registVehicle.push(
                    base[key][vehicleKey].registVehicle
                  );
                } else {
                  logVehicle(
                    `ℹ️ Matrícula cambiada no marcada para eliminación: ${base[key][vehicleKey].registVehicle}`
                  );
                }
              }

              result[key] = result[key] || {};
              result[key][vehicleKey] = object[key][vehicleKey];
            }
          });
        } else if (isObject(value) && isObject(object[key])) {
          result[key] = processDifferences(
            object[key],
            value,
            result[key] || {}
          );
        } else {
          result[key] = object[key];
        }
      }
    });
  }

  function arrayDiff(objectArray: any[], baseArray: any[]) {
    const addedArray = objectArray.filter((item) => !baseArray.includes(item));
    const removedArray = baseArray.filter(
      (item) => !objectArray.includes(item)
    );
    const modifiedArray = objectArray.filter((item) =>
      baseArray.includes(item)
    );
    return { addedArray, removedArray, modifiedArray };
  }

  processDifferences(object, base, differences);

  // Add fields in `object` not in `base` to `differences`
  Object.keys(object).forEach((key) => {
    if (!(key in base)) {
      if (key === 'letters') {
        // @ts-ignore
        differences[key] = [...(differences[key] || []), ...object[key]];
      } else if (key === 'vehicles') {
        differences[key] = differences[key] || {};

        // @ts-ignore
        Object.entries(object[key]).forEach(([vehicleKey, vehicleValue]) => {
          // @ts-ignore
          if (!(vehicleKey in base[key] || {})) {
            differences[key][vehicleKey] = vehicleValue;
          }
        });
      } else {
        // @ts-ignore
        differences[key] = object[key];
      }
    }
  });

  return { differences, removed, added, modified };
}

export function removeArraysAtFirstLevel(obj: any) {
  for (const key in obj) {
    if (Array.isArray(obj[key])) {
      delete obj[key];
    }
  }
  return obj;
}
